import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Subject, distinctUntilChanged, finalize, map, switchMap } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TagModule } from 'primeng/tag';
import { BackButton } from '@components/ui/back-button/back-button';
import { Loading } from '@components/ui/loading/loading';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { DataTableComponent, TableColumn, TableConfig } from '@components/data-table/data-table.component';
import { DateFormatLangPipe } from '@app/utils/pipes/date-format-lang-pipe';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import { operationDetailsUrl } from '@app/pages/admin/operation-details.util';
import { OrchestrationTableHeadingComponent } from '@app/pages/admin/components/orchestration-table-heading/orchestration-table-heading';
import { filterByCatalogQuery } from '@app/pages/admin/orchestration-catalog-search.util';
import { toRecordPageHeading } from '@app/pages/admin/orchestration-record-heading.util';
import { assignmentStateSeverity } from '@app/pages/admin/orchestration-process-page/orchestration-process-page.util';
import { AssignmentProcessService } from '@6grain/masofa-monolith-api-client/common/api/assignmentProcess.service';
import { ProcessEventTriggerExecutionResponse } from '@6grain/masofa-monolith-api-client/common/model/processEventTriggerExecutionResponse';
import { SatelliteProductAnalysisResponse } from '@6grain/masofa-monolith-api-client/common/model/satelliteProductAnalysisResponse';
import { SatelliteProductAnalysisStepResponse } from '@6grain/masofa-monolith-api-client/common/model/satelliteProductAnalysisStepResponse';

interface ProductStepView extends SatelliteProductAnalysisStepResponse {
  assignmentUrl: string;
}

interface ProductExecutionView extends ProcessEventTriggerExecutionResponse {
  sourceAssignmentUrl: string | null;
  targetAssignmentUrl: string | null;
  stateSeverity: ReturnType<typeof assignmentStateSeverity>;
}

@Component({
  selector: 'app-orchestration-satellite-product-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    TagModule,
    BackButton,
    Loading,
    SecondaryButton,
    DataTableComponent,
    DateFormatLangPipe,
    CopyableIdentifierComponent,
    OrchestrationTableHeadingComponent,
  ],
  templateUrl: './orchestration-satellite-product-page.html',
  styleUrl: './orchestration-satellite-product-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe],
})
export class OrchestrationSatelliteProductPageComponent implements OnInit, OnDestroy {
  private readonly api = inject(AssignmentProcessService);
  private readonly route = inject(ActivatedRoute);
  readonly translate = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();
  private readonly reload$ = new Subject<string>();

  private readonly stepKindTemplate = viewChild<TemplateRef<unknown>>('stepKindTemplate');
  private readonly stepAssignmentTemplate = viewChild<TemplateRef<unknown>>('stepAssignmentTemplate');
  private readonly stepStateTemplate = viewChild<TemplateRef<unknown>>('stepStateTemplate');
  private readonly executionStateTemplate = viewChild<TemplateRef<unknown>>('executionStateTemplate');
  private readonly executionPublisherTemplate = viewChild<TemplateRef<unknown>>('executionPublisherTemplate');
  private readonly executionStartedTemplate = viewChild<TemplateRef<unknown>>('executionStartedTemplate');

  readonly productId = signal('');
  readonly analysis = signal<SatelliteProductAnalysisResponse | null>(null);
  readonly steps = signal<ProductStepView[]>([]);
  readonly executions = signal<ProductExecutionView[]>([]);
  readonly stepSearch = signal('');
  readonly executionSearch = signal('');
  readonly loading = signal(false);
  readonly errorMessage = signal('');

  readonly pageHeading = computed(() => toRecordPageHeading({
    meaning: this.analysis()?.externalProductId,
    key: this.analysis()?.provider,
    id: this.analysis()?.id || this.productId(),
    fallbackTitle: 'Satellite product analysis',
  }));

  readonly snapshotRows = computed(() => {
    const product = this.analysis();
    if (!product) {
      return [];
    }
    return [
      { label: 'Satellite product ID', value: product.id ?? '', translateValue: false },
      { label: 'Provider', value: product.provider ?? '', translateValue: false },
      { label: 'Catalogue stage', value: product.processingStage ?? '', translateValue: true },
      { label: 'Download status', value: product.downloadState || '—', translateValue: true },
      { label: 'Cloud cover', value: product.cloudCover == null ? '—' : String(product.cloudCover), translateValue: false },
    ];
  });

  readonly visibleSteps = computed(() =>
    filterByCatalogQuery(
      this.steps(),
      this.stepSearch(),
      step => [step.kind, step.processDefinitionKey, step.assignmentId, step.assignmentState],
    ),
  );

  readonly visibleExecutions = computed(() =>
    filterByCatalogQuery(
      this.executions(),
      this.executionSearch(),
      execution => [execution.state, execution.sourceAssignmentId, execution.targetAssignmentId, execution.error],
    ),
  );

  readonly stepColumns = computed<TableColumn[]>(() => [
    {
      field: 'kind',
      header: 'Kind',
      type: 'custom',
      customTemplate: this.stepKindTemplate(),
    },
    { field: 'processDefinitionKey', header: 'Process', type: 'text' },
    {
      field: 'assignmentId',
      header: 'Assignment',
      type: 'custom',
      customTemplate: this.stepAssignmentTemplate(),
    },
    {
      field: 'assignmentState',
      header: 'Status',
      type: 'custom',
      customTemplate: this.stepStateTemplate(),
    },
    { field: 'at', header: 'Time', type: 'date' },
  ]);

  readonly executionColumns = computed<TableColumn[]>(() => [
    { field: 'startedAt', header: 'Started At', type: 'date' },
    {
      field: 'state',
      header: 'Status',
      type: 'custom',
      customTemplate: this.executionStateTemplate(),
    },
    {
      field: 'sourceAssignmentId',
      header: 'Published by',
      type: 'custom',
      customTemplate: this.executionPublisherTemplate(),
    },
    {
      field: 'targetAssignmentId',
      header: 'Started assignment',
      type: 'custom',
      customTemplate: this.executionStartedTemplate(),
    },
    { field: 'error', header: 'Error', type: 'text' },
  ]);

  readonly stepTableConfig: TableConfig = {
    paginator: false,
    keepOrder: true,
        emptyMessage: 'No related assignments',
  };

  readonly executionTableConfig: TableConfig = {
    paginator: false,
    keepOrder: true,
    emptyMessage: 'No executions for this event',
  };

  ngOnInit(): void {
    this.reload$.pipe(
      switchMap(productId => {
        this.loading.set(true);
        this.errorMessage.set('');
        this.analysis.set(null);
        this.steps.set([]);
        this.executions.set([]);
        return this.api.assignmentsSatelliteProductsProductIdGet(productId).pipe(
          map(body => this.applyAnalysis(body)),
          finalize(() => this.loading.set(false)),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      error: (error: unknown) => {
        this.loading.set(false);
        this.errorMessage.set(error instanceof HttpErrorResponse && error.status === 404
          ? 'No satellite product found'
          : 'Failed to load satellite product');
      },
    });

    this.route.paramMap.pipe(
      map(params => params.get('productId')?.trim() ?? ''),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(productId => {
      this.productId.set(productId);
      if (productId) {
        this.reload$.next(productId);
      }
    });
  }

  reload(): void {
    const productId = this.productId();
    if (productId) {
      this.reload$.next(productId);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyAnalysis(body: SatelliteProductAnalysisResponse): SatelliteProductAnalysisResponse {
    this.analysis.set(body);
    this.steps.set((body.steps ?? []).map(item => ({
      ...item,
      assignmentUrl: operationDetailsUrl('Assignment', item.assignmentId ?? ''),
    })));
    this.executions.set((body.triggerExecutions ?? []).map(item => ({
      ...item,
      sourceAssignmentUrl: item.sourceAssignmentId
        ? operationDetailsUrl('Assignment', item.sourceAssignmentId)
        : null,
      targetAssignmentUrl: item.targetAssignmentId
        ? operationDetailsUrl('Assignment', item.targetAssignmentId)
        : null,
      stateSeverity: assignmentStateSeverity(item.state),
    })));
    return body;
  }
}
